/**
 * ParentBenefitsRail — calm right-hand companion panel for the /more screen.
 *
 * Visible only on tablet + desktop (≥ lg breakpoint). Calls out the six
 * promises the upgrade spec asks us to surface for parents using Alouvya.
 * Each bullet pairs a circular pastel icon with a single warm line.
 */
import {
  Sparkles,
  Users,
  MessageCircle,
  CalendarCheck,
  HandHeart,
  ShieldCheck,
} from "lucide-react";

const BENEFITS = [
  {
    Icon: Sparkles,
    title: "Real-time updates",
    blurb: "Where they are, how they're feeling — kept gentle.",
    bg: "var(--pastel-mint)",
    fg: "#1F8A3F",
  },
  {
    Icon: Users,
    title: "Know who is with your child",
    blurb: "A name and a face for every quiet handover.",
    bg: "var(--pastel-lavender)",
    fg: "#1F3A2A",
  },
  {
    Icon: MessageCircle,
    title: "Easy communication",
    blurb: "One thread with the team. Encrypted before it leaves you.",
    bg: "var(--pastel-pink)",
    fg: "#BE185D",
  },
  {
    Icon: CalendarCheck,
    title: "Plan and stay ahead",
    blurb: "Field trips, holidays, permission slips — together.",
    bg: "var(--pastel-yellow)",
    fg: "#B45309",
  },
  {
    Icon: HandHeart,
    title: "Pickup with confidence",
    blurb: "Trusted adults only. One tap to let staff know.",
    bg: "var(--pastel-sky)",
    fg: "#2563EB",
  },
  {
    Icon: ShieldCheck,
    title: "Secure & private",
    blurb: "GDPR-aware, zero-trust, your data — yours.",
    bg: "var(--pastel-peach)",
    fg: "#C2410C",
  },
];

export default function ParentBenefitsRail() {
  return (
    <aside
      data-testid="parent-benefits-rail"
      className="hidden lg:block w-80 shrink-0 ml-10 pt-12"
      aria-label="Why families love Alouvya"
    >
      <span className="eyebrow">Why Alouvya</span>
      <h2 className="mt-3 font-display text-2xl text-[#1A1B2E] leading-tight tracking-tight">
        Peace of mind,
        <br />
        <span className="italic font-light text-[#1F3A2A]">every day.</span>
      </h2>
      <ul className="mt-8 space-y-5">
        {BENEFITS.map((b) => (
          <li
            key={b.title}
            data-testid={`benefit-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="flex items-start gap-4"
          >
            <span
              className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
              style={{ background: b.bg }}
              aria-hidden
            >
              <b.Icon size={18} strokeWidth={1.6} style={{ color: b.fg }} />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#1A1B2E] leading-snug">
                {b.title}
              </div>
              <div className="mt-1 text-sm text-[#6B7280] leading-relaxed">
                {b.blurb}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </aside>
  );
}
